import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import {
  getOrgRecipes,
  getOrgRecipe,
  saveOrgRecipe,
  applyOrgRecipe,
  deleteOrgRecipe,
  getInventorySettings,
  getInventoryCatalog,
  getMenu,
} from '../services/api';
import { inventoryErrorCode, inventoryErrorMessage } from '../utils/inventoryErrors';
import {
  normalizeRecipeName,
  buildOrgRecipePayload,
  withIngredientNames,
  menuProductNames,
} from '../utils/orgRecipes';
import { recipeTotalCost } from '../utils/recipeCost';
import { formatCurrency } from '../utils/formatCurrency';
import { getDateLocale } from '../utils/dateLocale';
import useEscapeKey from '../hooks/useEscapeKey';
import HelpIcon from '../components/help/HelpIcon';
import OrgRecipeLinesEditor from '../components/OrgRecipeLinesEditor';
import OrgRecipeApplicationSummary from '../components/OrgRecipeApplicationSummary';
import './OrgRecipesPage.css';

/** Insumo del catálogo con `cost` (el costo de referencia) para el editor y el costo estimado. */
const toEditorItem = (item) => ({
  ...item,
  cost: Number(item.refCost ?? item.cost) || 0,
});

/**
 * Recetas estándar de la organización (RES-45, épica D3): se define la receta de cada plato una
 * vez y baja a todas las sucursales, salvo las que eligieron receta local o ya tenían una propia.
 * Solo quien administra toda la organización edita (`can.orgRecipesWrite`); una sucursal la ve en
 * solo lectura.
 */
function OrgRecipesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = getDateLocale(i18n?.language);

  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Error de carga (el de axios): el mensaje se traduce al renderizar
  const [loadError, setLoadError] = useState(null);
  // null = todavía no se sabe (no se muestra la nota de solo lectura mientras carga)
  const [canWrite, setCanWrite] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [productNames, setProductNames] = useState([]);
  const [feedback, setFeedback] = useState(null);

  // Editor: { mode: 'create'|'edit', recipeKey, itemName, lines, version, apply }
  const [editor, setEditor] = useState(null);
  const [editorError, setEditorError] = useState('');
  const [saving, setSaving] = useState(false);

  // Locales de una receta (vista previa): { recipeKey, itemName, loading, error, data }
  const [detail, setDetail] = useState(null);
  // Resultado del último guardado / "Aplicar ahora": { kind, applied, recipe, applications, summary }
  const [result, setResult] = useState(null);
  const [applyingKey, setApplyingKey] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const formatDateTime = useCallback(
    (iso) => {
      if (!iso) return '—';
      const date = new Date(iso);
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(locale);
    },
    [locale]
  );

  const loadRecipes = useCallback(async () => {
    try {
      const response = await getOrgRecipes();
      const list = Array.isArray(response?.recipes) ? response.recipes : [];
      setRecipes(
        [...list].sort((a, b) => String(a.itemName).localeCompare(String(b.itemName), 'es'))
      );
      setLoadError(null);
    } catch (error) {
      console.error('Error loading org recipes:', error);
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecipes();
    getInventorySettings()
      .then((settings) => setCanWrite(settings?.can?.orgRecipesWrite === true))
      .catch(() => setCanWrite(false));
    getInventoryCatalog()
      .then((response) => setCatalog((response?.items || []).map(toEditorItem)))
      .catch(() => setCatalog([]));
    // Sugerencias de nombre: la carta del local seleccionado
    getMenu()
      .then((response) => setProductNames(menuProductNames(response?.menu)))
      .catch(() => setProductNames([]));
  }, [loadRecipes]);

  const recipesByKey = useMemo(() => new Map(recipes.map((r) => [r.recipeKey, r])), [recipes]);

  // --- Editor ---
  const openCreate = () => {
    setEditorError('');
    setEditor({
      mode: 'create',
      recipeKey: null,
      itemName: '',
      lines: [],
      version: 0,
      apply: true,
    });
  };

  const openEdit = (recipe) => {
    setEditorError('');
    setDetail(null);
    setEditor({
      mode: 'edit',
      recipeKey: recipe.recipeKey,
      itemName: recipe.itemName,
      lines: withIngredientNames(recipe.recipe, catalog),
      version: recipe.version || 0,
      apply: true,
    });
  };

  const closeEditor = () => {
    if (saving) return;
    setEditor(null);
    setEditorError('');
  };

  const updateEditor = (patch) => setEditor((prev) => (prev ? { ...prev, ...patch } : prev));

  // Al crear, un nombre que ya tiene receta estándar se edita en vez de duplicarse
  const duplicateRecipe =
    editor && editor.mode === 'create'
      ? recipesByKey.get(normalizeRecipeName(editor.itemName)) || null
      : null;

  const editorValid =
    Boolean(editor) &&
    editor.itemName.trim().length > 0 &&
    editor.lines.length > 0 &&
    !duplicateRecipe;

  const reloadAfterConflict = async (key) => {
    try {
      const fresh = await getOrgRecipe(key);
      if (!fresh?.recipe) return;
      setEditor((prev) =>
        prev
          ? {
              ...prev,
              mode: 'edit',
              recipeKey: fresh.recipe.recipeKey,
              itemName: fresh.recipe.itemName,
              lines: withIngredientNames(fresh.recipe.recipe, catalog),
              version: fresh.recipe.version || 0,
            }
          : prev
      );
    } catch (reloadError) {
      console.error('Error reloading org recipe after conflict:', reloadError);
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!editorValid || saving) return;
    setSaving(true);
    setEditorError('');
    const payload = buildOrgRecipePayload({
      itemName: editor.itemName,
      lines: editor.lines,
      version: editor.mode === 'edit' ? editor.version : 0,
      apply: editor.apply,
    });
    try {
      const response = await saveOrgRecipe(payload);
      setEditor(null);
      setFeedback(null);
      setResult({
        kind: editor.mode === 'create' ? 'created' : 'saved',
        applied: payload.apply,
        recipe: response?.recipe || null,
        applications: response?.applications || [],
        summary: response?.summary || null,
      });
      await loadRecipes();
    } catch (error) {
      const message = inventoryErrorMessage(t, error, 'inventory.orgRecipes.errors.save');
      if (inventoryErrorCode(error) === 'VERSION_CONFLICT') {
        // Otro guardó antes: se trae la versión vigente al editor para no pisarla a ciegas
        await reloadAfterConflict(editor.recipeKey || normalizeRecipeName(payload.itemName));
        setEditorError(`${message} ${t('inventory.orgRecipes.editor.conflictReloaded')}`);
        loadRecipes();
      } else {
        setEditorError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  // --- Locales (vista previa, no escribe nada) ---
  const openDetail = async (recipe) => {
    setDetail({
      recipeKey: recipe.recipeKey,
      itemName: recipe.itemName,
      loading: true,
      error: '',
      data: null,
    });
    try {
      const data = await getOrgRecipe(recipe.recipeKey);
      setDetail((prev) =>
        prev && prev.recipeKey === recipe.recipeKey ? { ...prev, loading: false, data } : prev
      );
    } catch (error) {
      const message = inventoryErrorMessage(t, error, 'inventory.orgRecipes.errors.detail');
      setDetail((prev) =>
        prev && prev.recipeKey === recipe.recipeKey
          ? { ...prev, loading: false, error: message }
          : prev
      );
    }
  };

  const closeDetail = () => setDetail(null);

  // --- Aplicar ahora ---
  const handleApply = async (recipe) => {
    if (applyingKey) return;
    setApplyingKey(recipe.recipeKey);
    setFeedback(null);
    try {
      const response = await applyOrgRecipe(recipe.recipeKey);
      setDetail(null);
      setResult({
        kind: 'applied',
        applied: true,
        recipe: response?.recipe || recipe,
        applications: response?.applications || [],
        summary: response?.summary || null,
      });
      await loadRecipes();
    } catch (error) {
      setFeedback({
        type: 'error',
        message: inventoryErrorMessage(t, error, 'inventory.orgRecipes.errors.apply'),
      });
    } finally {
      setApplyingKey(null);
    }
  };

  // --- Borrar ---
  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const response = await deleteOrgRecipe(deleteTarget.recipeKey);
      setFeedback({
        type: 'success',
        message: t('inventory.orgRecipes.delete.success', {
          name: deleteTarget.itemName,
          count: Number(response?.detached) || 0,
        }),
      });
      if (result?.recipe?.recipeKey === deleteTarget.recipeKey) setResult(null);
      setDeleteTarget(null);
      await loadRecipes();
    } catch (error) {
      setDeleteTarget(null);
      setFeedback({
        type: 'error',
        message: inventoryErrorMessage(t, error, 'inventory.orgRecipes.errors.delete'),
      });
    } finally {
      setDeleting(false);
    }
  };

  useEscapeKey(Boolean(deleteTarget) && !deleting, () => setDeleteTarget(null));
  useEscapeKey(Boolean(editor) && !deleteTarget, closeEditor);
  useEscapeKey(Boolean(detail) && !editor && !deleteTarget, closeDetail);

  // --- Render ---
  const renderResult = () => {
    if (!result) return null;
    const titleKey = {
      applied: 'inventory.orgRecipes.result.appliedTitle',
      created: 'inventory.orgRecipes.result.createdTitle',
      saved: 'inventory.orgRecipes.result.savedTitle',
    }[result.kind];
    return (
      <section className="org-recipes-result" role="status" data-testid="org-recipes-result">
        <div className="org-recipes-result-header">
          <h2>
            {t(titleKey, { name: result.recipe?.itemName || '', version: result.recipe?.version })}
          </h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setResult(null)}>
            {t('common.close')}
          </button>
        </div>
        {result.applied ? (
          <>
            <p className="org-recipe-muted">
              {t('inventory.orgRecipes.result.following', {
                count: Number(result.summary?.following) || 0,
              })}
            </p>
            <OrgRecipeApplicationSummary
              applications={result.applications}
              summary={result.summary}
            />
          </>
        ) : (
          <p className="org-recipe-muted">{t('inventory.orgRecipes.result.notApplied')}</p>
        )}
      </section>
    );
  };

  const renderList = () => {
    if (loading) return <p className="org-recipe-muted">{t('common.loading')}</p>;
    if (loadError) {
      return (
        <div className="alert alert-error" role="alert">
          {inventoryErrorMessage(t, loadError, 'inventory.orgRecipes.errors.load')}{' '}
          <button type="button" className="btn btn-sm btn-secondary" onClick={loadRecipes}>
            {t('common.retry')}
          </button>
        </div>
      );
    }
    if (recipes.length === 0) {
      return (
        <div className="org-recipes-empty">
          <p>{t('inventory.orgRecipes.empty')}</p>
          {canWrite && (
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} />
              {t('inventory.orgRecipes.new')}
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="org-recipes-table-wrapper">
        <table className="org-recipes-table">
          <thead>
            <tr>
              <th>{t('inventory.orgRecipes.columns.product')}</th>
              <th>{t('inventory.orgRecipes.columns.version')}</th>
              <th className="num">{t('inventory.orgRecipes.columns.ingredients')}</th>
              <th>{t('inventory.orgRecipes.columns.updatedAt')}</th>
              <th>{t('inventory.orgRecipes.columns.lastAppliedAt')}</th>
              <th className="actions">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {recipes.map((recipe) => (
              <tr key={recipe.recipeKey} data-recipe={recipe.recipeKey}>
                <td className="org-recipes-name">{recipe.itemName}</td>
                <td>v{recipe.version}</td>
                <td className="num">{(recipe.recipe || []).length}</td>
                <td>{formatDateTime(recipe.updatedAt)}</td>
                <td>
                  {recipe.lastAppliedAt
                    ? formatDateTime(recipe.lastAppliedAt)
                    : t('inventory.orgRecipes.neverApplied')}
                </td>
                <td className="actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    data-action="view"
                    onClick={() => openDetail(recipe)}
                  >
                    {t('inventory.orgRecipes.actions.view')}
                  </button>
                  {canWrite && (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        data-action="edit"
                        onClick={() => openEdit(recipe)}
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        data-action="apply"
                        disabled={Boolean(applyingKey)}
                        onClick={() => handleApply(recipe)}
                      >
                        {applyingKey === recipe.recipeKey
                          ? t('inventory.orgRecipes.actions.applying')
                          : t('inventory.orgRecipes.actions.apply')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        data-action="delete"
                        onClick={() => setDeleteTarget(recipe)}
                      >
                        {t('common.delete')}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderEditor = () => {
    if (!editor) return null;
    const isCreate = editor.mode === 'create';
    return (
      <div className="modal-overlay" onClick={closeEditor}>
        <div
          className="modal-content modal-content--wide org-recipe-editor-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="org-recipe-editor-title"
          onClick={(e) => e.stopPropagation()}
        >
          <form onSubmit={handleSave} noValidate>
            <div className="modal-header">
              <h2 id="org-recipe-editor-title">
                {isCreate
                  ? t('inventory.orgRecipes.editor.createTitle')
                  : t('inventory.orgRecipes.editor.editTitle', {
                      name: editor.itemName,
                      version: editor.version,
                    })}
              </h2>
            </div>
            <div className="modal-body">
              <div className="org-recipe-field">
                <label htmlFor="org-recipe-name">{t('inventory.orgRecipes.editor.itemName')}</label>
                <input
                  id="org-recipe-name"
                  type="text"
                  list="org-recipe-product-names"
                  value={editor.itemName}
                  readOnly={!isCreate}
                  maxLength={120}
                  placeholder={t('inventory.orgRecipes.editor.itemNamePlaceholder')}
                  onChange={(e) => updateEditor({ itemName: e.target.value })}
                />
                {isCreate && (
                  <datalist id="org-recipe-product-names">
                    {productNames.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                )}
                <p className="org-recipe-hint">
                  {isCreate
                    ? t('inventory.orgRecipes.editor.itemNameHint')
                    : t('inventory.orgRecipes.editor.itemNameLocked')}
                </p>
                {duplicateRecipe && (
                  <p className="org-recipe-warning" role="alert" data-testid="org-recipe-duplicate">
                    {t('inventory.orgRecipes.editor.duplicate', {
                      name: duplicateRecipe.itemName,
                    })}{' '}
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => openEdit(duplicateRecipe)}
                    >
                      {t('inventory.orgRecipes.editor.editExisting')}
                    </button>
                  </p>
                )}
              </div>

              <div className="recipe-builder">
                <h4>
                  <span className="recipe-title">{t('menu.recipe.ingredients')}</span>
                  <span className="cost-label">
                    {t('inventory.orgRecipes.editor.estimatedCost')}:{' '}
                    <strong>{formatCurrency(recipeTotalCost(editor.lines, catalog))}</strong>
                  </span>
                </h4>
                <OrgRecipeLinesEditor
                  lines={editor.lines}
                  onChange={(lines) => updateEditor({ lines })}
                  inventoryItems={catalog}
                  idPrefix="org-recipe"
                />
              </div>

              <label className="org-recipe-apply-toggle">
                <input
                  id="org-recipe-apply"
                  type="checkbox"
                  checked={editor.apply}
                  onChange={(e) => updateEditor({ apply: e.target.checked })}
                />
                <span>
                  {t('inventory.orgRecipes.editor.apply')}
                  <span className="org-recipe-hint">
                    {t('inventory.orgRecipes.editor.applyHint')}
                  </span>
                </span>
              </label>

              {editorError && (
                <div
                  className="alert alert-error"
                  role="alert"
                  data-testid="org-recipe-editor-error"
                >
                  {editorError}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={closeEditor}
                disabled={saving}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                data-action="save"
                disabled={!editorValid || saving}
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderDetail = () => {
    if (!detail) return null;
    const recipe = detail.data?.recipe || recipesByKey.get(detail.recipeKey) || null;
    return (
      <div className="modal-overlay" onClick={closeDetail}>
        <div
          className="modal-content modal-content--wide org-recipe-detail-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="org-recipe-detail-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2 id="org-recipe-detail-title">
              {t('inventory.orgRecipes.detail.title', {
                name: detail.itemName,
                version: recipe?.version,
              })}
            </h2>
          </div>
          <div className="modal-body">
            {detail.loading && <p className="org-recipe-muted">{t('common.loading')}</p>}
            {detail.error && (
              <div className="alert alert-error" role="alert">
                {detail.error}
              </div>
            )}
            {detail.data && (
              <>
                <h3 className="org-recipe-subtitle">{t('menu.recipe.ingredients')}</h3>
                <OrgRecipeLinesEditor
                  lines={withIngredientNames(recipe?.recipe, catalog)}
                  onChange={() => {}}
                  inventoryItems={catalog}
                  readOnly
                  idPrefix="org-recipe-detail"
                />
                <h3 className="org-recipe-subtitle">
                  {t('inventory.orgRecipes.detail.restaurants')}
                </h3>
                <p className="org-recipe-hint">{t('inventory.orgRecipes.detail.previewNote')}</p>
                <OrgRecipeApplicationSummary
                  applications={detail.data.applications}
                  summary={detail.data.summary}
                />
              </>
            )}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={closeDetail}>
              {t('common.close')}
            </button>
            {canWrite && recipe && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => openEdit(recipe)}
                >
                  {t('common.edit')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-action="apply"
                  disabled={Boolean(applyingKey)}
                  onClick={() => handleApply(recipe)}
                >
                  {applyingKey === recipe.recipeKey
                    ? t('inventory.orgRecipes.actions.applying')
                    : t('inventory.orgRecipes.actions.apply')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteConfirm = () => {
    if (!deleteTarget) return null;
    return (
      <div className="modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
        <div
          className="modal-content"
          role="dialog"
          aria-modal="true"
          aria-labelledby="org-recipe-delete-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2 id="org-recipe-delete-title">
              {t('inventory.orgRecipes.delete.title', { name: deleteTarget.itemName })}
            </h2>
          </div>
          <div className="modal-body">
            <p>{t('inventory.orgRecipes.delete.body', { name: deleteTarget.itemName })}</p>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              data-action="confirm-delete"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {t('inventory.orgRecipes.delete.confirm')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="org-recipes-page">
      <div className="org-recipes-header">
        <button
          type="button"
          className="btn btn-ghost btn-sm org-recipes-back"
          onClick={() => navigate('/inventory')}
        >
          <ArrowLeft size={16} />
          {t('inventory.orgRecipes.back')}
        </button>
        <div className="org-recipes-title-row">
          <h1>
            {t('inventory.orgRecipes.title')}
            <HelpIcon topicKey="inventory.orgRecipes" />
          </h1>
          {canWrite && recipes.length > 0 && (
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} />
              {t('inventory.orgRecipes.new')}
            </button>
          )}
        </div>
        <p className="org-recipes-subtitle">{t('inventory.orgRecipes.subtitle')}</p>
        {canWrite === false && (
          <div className="alert alert-warning" data-testid="org-recipes-readonly">
            {t('inventory.orgRecipes.readOnlyNote')}
          </div>
        )}
      </div>

      {feedback && (
        <div
          className={`alert alert-${feedback.type === 'error' ? 'error' : 'success'}`}
          role={feedback.type === 'error' ? 'alert' : 'status'}
          data-testid="org-recipes-feedback"
        >
          {feedback.message}
        </div>
      )}

      {renderResult()}
      {renderList()}
      {renderEditor()}
      {renderDetail()}
      {renderDeleteConfirm()}
    </div>
  );
}

export default OrgRecipesPage;
